import React from "react";
import { useLang } from "../../context/LanguageContext";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { buildWaLink, IMAGES, ADDRESS_SUITE } from "../../lib/constants";
import { Reveal } from "./Reveal";
import { Waves, Flame, DoorOpen, Wind, ArrowUpRight } from "lucide-react";

const featureIcons = [Waves, Flame, DoorOpen, Wind];

export const SuiteMaximo = () => {
  const { t, lang } = useLang();

  const waMessage =
    lang === "it"
      ? "Ciao, vorrei informazioni sulla Suite maXimo con vasca idromassaggio."
      : "Hello, I'd like information about Suite maXimo with hot tub.";

  return (
    <section
      id="suite"
      data-testid="suite-section"
      className="relative py-24 md:py-40 bg-stone-950 text-stone-50 overflow-hidden"
    >
      {/* Decorative warm glows */}
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-amber-600/10 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-orange-700/8 blur-3xl pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT: Title + features */}
          <div className="lg:col-span-6">
            <Reveal delay={60}>
              <div className="font-heading text-amber-300/90 text-lg md:text-xl tracking-widest uppercase mb-4">
                {t.suite.label}
              </div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-stone-50">
                {t.suite.title}
              </h2>
            </Reveal>
            <Reveal delay={180}>
              <p className="mt-8 text-stone-300 font-light leading-relaxed text-lg max-w-xl">
                {t.suite.lead}
              </p>
            </Reveal>

            {/* Features grid */}
            <Reveal delay={240}>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-stone-800 border border-stone-800">
                {t.suite.features.map((f, i) => {
                  const Icon = featureIcons[i];
                  const highlight = i < 2;
                  return (
                    <div
                      key={i}
                      data-testid={`suite-feature-${i}`}
                      className={`p-6 md:p-8 ${
                        highlight ? "bg-stone-900" : "bg-stone-950"
                      }`}
                    >
                      <Icon
                        className={`w-7 h-7 mb-4 stroke-[1.3] ${
                          highlight ? "text-amber-300" : "text-stone-400"
                        }`}
                      />
                      <h3
                        className={`font-heading text-2xl ${
                          highlight ? "text-amber-100" : "text-stone-100"
                        }`}
                      >
                        {f.title}
                      </h3>
                      <p className="mt-2 text-sm text-stone-400 font-light leading-relaxed">
                        {f.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </Reveal>

            {/* Details */}
            <Reveal delay={300}>
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.25em] text-stone-400">
                <span>
                  <span className="text-amber-300/90">·</span> {t.suite.details.type}
                </span>
                <span>
                  <span className="text-amber-300/90">·</span> {t.suite.details.beds}
                </span>
                <span>
                  <span className="text-amber-300/90">·</span> {t.suite.details.rooms}
                </span>
                <span>
                  <span className="text-amber-300/90">·</span> {t.suite.details.wifi}
                </span>
              </div>
            </Reveal>

            <Reveal delay={360}>
              <a
                href={buildWaLink(waMessage)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="suite-whatsapp-cta"
                className="group mt-10 inline-flex items-center gap-3 bg-[#25D366] text-white px-8 py-5 text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#1faa52] transition-all duration-300 shadow-2xl hover:-translate-y-0.5"
              >
                <WhatsAppIcon className="w-5 h-5" />
                {t.suite.cta}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </Reveal>

            <Reveal delay={420}>
              <div className="mt-6 text-[11px] uppercase tracking-[0.25em] text-stone-500">
                {ADDRESS_SUITE}
              </div>
            </Reveal>
          </div>

          {/* RIGHT: Image collage — hot tub featured */}
          <div className="lg:col-span-6">
            <div className="grid grid-cols-6 gap-3 md:gap-4">
              <Reveal delay={120} className="col-span-6">
                <div className="image-hover aspect-[16/10] bg-stone-800 overflow-hidden">
                  <img
                    src={IMAGES.suite1}
                    alt="Suite maXimo — vasca idromassaggio Bari Palese"
                    className="w-full h-full object-cover"
                  />
                </div>
              </Reveal>
              <Reveal delay={200} className="col-span-3">
                <div className="image-hover aspect-square bg-stone-800 overflow-hidden">
                  <img
                    src={IMAGES.suite2}
                    alt="Suite maXimo — camino e zona living"
                    className="w-full h-full object-cover"
                  />
                </div>
              </Reveal>
              <Reveal delay={260} className="col-span-3">
                <div className="image-hover aspect-square bg-stone-800 overflow-hidden">
                  <img
                    src={IMAGES.suite3}
                    alt="Suite maXimo — interno suite"
                    className="w-full h-full object-cover"
                  />
                </div>
              </Reveal>
              <Reveal delay={320} className="col-span-2">
                <div className="image-hover aspect-square bg-stone-800 overflow-hidden">
                  <img
                    src={IMAGES.suite4}
                    alt="Suite maXimo dettaglio"
                    className="w-full h-full object-cover"
                  />
                </div>
              </Reveal>
              <Reveal delay={360} className="col-span-2">
                <div className="image-hover aspect-square bg-stone-800 overflow-hidden">
                  <img
                    src={IMAGES.suite5}
                    alt="Suite maXimo ambiente"
                    className="w-full h-full object-cover"
                  />
                </div>
              </Reveal>
              <Reveal delay={400} className="col-span-2">
                <div className="image-hover aspect-square bg-stone-800 overflow-hidden">
                  <img
                    src={IMAGES.suite6}
                    alt="Suite maXimo zona relax"
                    className="w-full h-full object-cover"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
